import React, { useState } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Box,
   Typography,
   TextField,
   Button,
   IconButton,
   CircularProgress,
   Snackbar,
   Alert,
   Link,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

interface SignupModalProps {
   open: boolean;
   onClose: () => void;
   referrerId?: string | null;
   source?: string | null;
   onSignupSuccess?: (user: any) => void;
}

const SignupModal: React.FC<SignupModalProps> = ({
   open,
   onClose,
   referrerId,
   source,
   onSignupSuccess,
}) => {
   const navigate = useNavigate();
   const auth = getAuth();

   const [fullName, setFullName] = useState('');
   const [email, setEmail] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [submitting, setSubmitting] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

   const handleRegistration = async () => {
      // Validation
      if (!fullName.trim() || !email.trim() || !password.trim()) {
         setSnackbarMessage('Please fill in all required fields');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password !== confirmPassword) {
         setSnackbarMessage('Passwords do not match');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      if (password.length < 6) {
         setSnackbarMessage('Password must be at least 6 characters');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
         return;
      }

      setSubmitting(true);
      try {
         console.log('📤 Profile referral signup data:', {
            referrerId,
            source,
         });

         // Register user via profile referral endpoint
         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/auth/register-with-referral`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
               },
               body: JSON.stringify({
                  fullName,
                  email,
                  password,
                  referrerId: referrerId || null,
                  source: source || 'profile_qr',
               }),
            }
         );

         const result = await response.json();

         if (result.success) {
            setSnackbarMessage('Account created successfully! Redirecting to dashboard...');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Log in the user with custom token
            if (result.token) {
               try {
                  const userCredential = await signInWithCustomToken(auth, result.token);
                  console.log('✅ User logged in successfully:', userCredential.user.uid);

                  // Wait for auth state to fully update
                  await new Promise((resolve) => setTimeout(resolve, 500));

                  // Call success callback if provided
                  if (onSignupSuccess && result.user) {
                     // Pass the full result including tracking flags
                     onSignupSuccess({
                        ...result.user,
                        contactWaitingCreated: result.contactWaitingCreated,
                        networkRequestCreated: result.networkRequestCreated,
                        signupTracked: result.signupTracked,
                     });
                  }

                  // Close modal
                  onClose();

                  // Redirect to dashboard
                  console.log('🔄 Redirecting to dashboard...');
                  window.location.href = '/dashboard';
               } catch (authError) {
                  console.error('❌ Error signing in with custom token:', authError);
                  setSnackbarMessage('Login failed. Please try signing in manually.');
                  setSnackbarSeverity('error');
                  setSnackbarOpen(true);
               }
            } else {
               // No token provided, just close
               setTimeout(() => {
                  onClose();
                  setSnackbarMessage('Registration successful! Please sign in.');
                  setSnackbarSeverity('success');
                  setSnackbarOpen(true);
               }, 1500);
            }
         } else {
            setSnackbarMessage(result.error || 'Registration failed. Please try again.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error registering:', err);
         setSnackbarMessage('Registration failed. Please try again.');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         setSubmitting(false);
      }
   };

   const handleClose = () => {
      if (!submitting) {
         onClose();
      }
   };

   return (
      <>
         <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
            <DialogTitle>
               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant='h6'>Create Account</Typography>
                  <IconButton onClick={handleClose} disabled={submitting}>
                     <CloseIcon />
                  </IconButton>
               </Box>
            </DialogTitle>
            <DialogContent>
               <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                  Enter your details to create an account and connect.
               </Typography>

               <TextField
                  fullWidth
                  label='Full Name'
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Email Address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  margin='normal'
                  required
               />

               <TextField
                  fullWidth
                  label='Password'
                  type='password'
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  margin='normal'
                  required
                  helperText='At least 6 characters'
               />

               <TextField
                  fullWidth
                  label='Confirm Password'
                  type='password'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  margin='normal'
                  required
               />

               {/* Sign In Link */}
               <Box sx={{ mt: 2, textAlign: 'center' }}>
                  <Typography variant='body2' color='text.secondary'>
                     Already have an account?{' '}
                     <Link
                        href='/login'
                        underline='hover'
                        sx={{ color: '#01949A', fontWeight: 600 }}
                     >
                        Sign In
                     </Link>
                  </Typography>
               </Box>
            </DialogContent>
            <DialogActions>
               <Button onClick={handleClose} disabled={submitting}>
                  Cancel
               </Button>
               <Button onClick={handleRegistration} variant='contained' disabled={submitting}>
                  {submitting ? <CircularProgress size={24} /> : 'Create Account'}
               </Button>
            </DialogActions>
         </Dialog>

         {/* Snackbar for notifications */}
         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </>
   );
};

export default SignupModal;
